// /wms/product เปิด/ปิดการใช้งานสินค้า WMS (`wms_product.is_active`)
// spec docs/specs/2026-10-07-wms-product-active-design.md §3 F1–F6 · bo-fn #416 · bo-api #834
//
// BE contract (B2/B3/B5):
//   GET /api/v1/wms/product?active=1|0|all   (default 1; rows carry is_active)
//   POST /api/v1/wms/product/:id/active {is_active: 0|1} -> row + stock_balance
//       (POST, never PUT: an older BE sends PUT /wms/product/:id/* to product-update)
//       503 Msg WMS_PRODUCT_ACTIVE_COLUMN_MISSING until the owner runs the SQL
//   barcode lookups: 404 Msg WMS_PRODUCT_INACTIVE for a disabled product

export const ACTIVE_ON = "1";
export const ACTIVE_OFF = "0";
export const ACTIVE_ALL = "all";
export const ACTIVE_FILTERS = [ACTIVE_ON, ACTIVE_OFF, ACTIVE_ALL];
// F2: a row that left the current filter stays visible this long (the toast is read first).
export const HIDE_DELAY_MS = 600;

export const ERR_COLUMN_MISSING = "WMS_PRODUCT_ACTIVE_COLUMN_MISSING";
export const ERR_INACTIVE = "WMS_PRODUCT_INACTIVE";
// FE-only: the toggle answered 2xx without is_active (not the #834 handler) —
// treated as a failure: the switch stays put, generic error, list reloaded.
export const ERR_BAD_RESPONSE = "WMS_PRODUCT_ACTIVE_BAD_RESPONSE";

export function badToggleResponseError() {
    const err = new Error(ERR_BAD_RESPONSE);
    err.code = ERR_BAD_RESPONSE;
    return err;
}

export function isBadToggleResponse(err) {
    return !!(err && err.code === ERR_BAD_RESPONSE);
}

// URL / dispatch value -> "1" | "0" | "all". Anything unknown (missing query,
// a click event handed to listData by an old @click binding) = "1", the BE default.
export function activeParam(value) {
    if (value === null || value === undefined) return ACTIVE_ON;
    const v = String(value).trim().toLowerCase();
    if (v === ACTIVE_ALL) return ACTIVE_ALL;
    if (v === "0" || v === "false") return ACTIVE_OFF;
    return ACTIVE_ON;
}

// A row from a BE without the column (SQL not run yet) has no is_active: every
// product is active then (§4 acceptance 1).
export function isRowActive(row) {
    if (!row || row.is_active === undefined || row.is_active === null) return true;
    return Number(row.is_active) !== 0;
}

// Stock on hand of a list row: GET /api/v1/wms/product returns `Amount` = sum of
// wms_inventory.balance (number or null; tolerate a {Int64, Valid} NullInt64).
export function rowStock(row) {
    if (!row) return 0;
    let a = row.Amount;
    if (a && typeof a === "object") a = a.Valid === false ? 0 : a.Int64;
    const n = Number(a);
    return isFinite(n) ? n : 0;
}

export function escapeHtml(value) {
    return String(value === undefined || value === null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

// Swal content for the switch (F2). `next` = the state the switch asks for.
// `title` carries the product name -> pass it as swal `titleText` (plain text).
// Disabling a product with stock (row.Amount > 0) adds a red "คงเหลือในคลัง {n} ชิ้น"
// line; `html` is escaped, `stock` is the number shown (0 = no line).
export function toggleConfirmText(t, row, next) {
    const name = (row && (row.Name || row.name)) || "";
    if (next) {
        const text = t("wms_product_active.confirm_enable_text");
        return {
            title: t("wms_product_active.confirm_enable", { name }),
            text,
            html: "<div>" + escapeHtml(text) + "</div>",
            stock: 0,
            confirmClass: "btn btn-success btn-fill"
        };
    }
    const text = t("wms_product_active.confirm_disable_text");
    const stock = rowStock(row);
    let html = "<div>" + escapeHtml(text) + "</div>";
    if (stock > 0) {
        html += '<div class="text-danger wms-stock-left">' + escapeHtml(t("wms_product_active.stock_left", { n: stock })) + "</div>";
    }
    return {
        title: t("wms_product_active.confirm_disable", { name }),
        text,
        html,
        stock: stock > 0 ? stock : 0,
        confirmClass: "btn btn-danger btn-fill"
    };
}

// Toggle response -> { is_active: 0|1, stock_balance: number }, or null when the
// body has no is_active at all (unexpected shape: not the #834 handler). Accepts
// the bare row, {Data: row}, {Data: [row]} and {Datum: row}; stock_balance may sit beside Data.
export function parseToggleResponse(resp) {
    const body = (resp && resp.data) || {};
    let data = body.Data || body.Datum || body.data || body;
    if (Array.isArray(data)) data = data[0] || {};
    const pick = (k) => (data && data[k] !== undefined && data[k] !== null ? data[k] : body[k]);
    const rawActive = pick("is_active");
    if (rawActive === undefined || rawActive === null) return null;
    const stock = Number(pick("stock_balance"));
    return { is_active: Number(rawActive) !== 0 ? 1 : 0, stock_balance: isFinite(stock) ? stock : 0 };
}

// Updates the row with ID `id` in place. `set` = Vue's $set in the page (a row
// from an old BE has no is_active key); plain assignment otherwise.
export function applyToggleLocally(rows, id, isActive, set) {
    const assign = typeof set === "function" ? set : (o, k, v) => { o[k] = v; };
    const row = (rows || []).find((r) => r && String(r.ID) === String(id));
    if (!row) return null;
    assign(row, "is_active", isActive ? 1 : 0);
    return row;
}

// Rows the list endpoint returned, with is_active always present (reactive).
export function normalizeRows(rows) {
    return (Array.isArray(rows) ? rows : []).map((r) =>
        r && (r.is_active === undefined || r.is_active === null) ? Object.assign({}, r, { is_active: 1 }) : r
    );
}

// After a toggle: does the row leave the list shown under `filter`?
export function shouldHideAfterToggle(filter, isActive) {
    const f = activeParam(filter);
    if (f === ACTIVE_ON) return !isActive;
    if (f === ACTIVE_OFF) return !!isActive;
    return false;
}

// Success toast (F2). Disabling a product that still has stock -> warning type
// with the remaining quantity (the confirm shows row.Amount; this uses the POST response).
export function toggleToast(t, isActive, stockBalance) {
    if (isActive) return { type: "success", message: t("wms_product_active.toggled_on") };
    const n = Number(stockBalance) || 0;
    if (n > 0) {
        return {
            type: "warning",
            message: t("wms_product_active.toggled_off") + " · " + t("wms_product_active.stock_left", { n })
        };
    }
    return { type: "success", message: t("wms_product_active.toggled_off") };
}

// §7.1 F7b (#421): ids of the products in the active list (WmsProduct/getData),
// as strings. null while that list is empty / not loaded / failed -> "do not filter".
export function activeProductIdSet(products) {
    if (!Array.isArray(products) || products.length === 0) return null;
    const ids = new Set();
    products.forEach((p) => {
        if (p && p.ID !== undefined && p.ID !== null) ids.add(String(p.ID));
    });
    return ids.size === 0 ? null : ids;
}

// §7.1 F7b: WmsInventory/getData rows (go-wms, no is_active filter) minus rows
// whose ProductID is not in the active product list. `active` is the product
// list or a Set from activeProductIdSet. Empty / not loaded -> rows unchanged,
// so a missing product list never blanks the table.
export function filterRowsByActiveProducts(rows, active) {
    const list = Array.isArray(rows) ? rows : [];
    const ids = active instanceof Set ? (active.size === 0 ? null : active) : activeProductIdSet(active);
    if (!ids) return list;
    return list.filter((d) => d && ids.has(String(d.ProductID)));
}

// §8 F8 (#424): ids of the DISABLED products in the active=all list
// (WmsProduct/getDataAll), as strings. Empty / not loaded / a BE without the
// column (no is_active) -> empty Set, so nothing is disabled on the order forms.
export function inactiveWmsIdSet(products) {
    const ids = new Set();
    if (!Array.isArray(products)) return ids;
    products.forEach((p) => {
        if (!p || p.ID === undefined || p.ID === null) return;
        if (p.is_active === undefined || p.is_active === null || p.is_active === "") return;
        if (Number(p.is_active) === 0) ids.add(String(p.ID));
    });
    return ids;
}

// §8 F8: a BO SKU option (attribute option with wms_map[]) is not selectable
// when any mapped WMS product (non-zero wms_product_id) is disabled.
// wms_product_random entries (random bundles) are not evaluated (out of scope).
export function skuOptionWmsInactive(option, inactiveIds) {
    if (!(inactiveIds instanceof Set) || inactiveIds.size === 0) return false;
    const map = option && Array.isArray(option.wms_map) ? option.wms_map : [];
    return map.some((m) => {
        if (!m) return false;
        const id = m.wms_product_id;
        if (id === undefined || id === null || id === "" || Number(id) === 0) return false;
        return inactiveIds.has(String(id));
    });
}

// §8.1 F8b (#424): the add paths that skip the dropdown (auto-add of a product
// whose attributes each have one option, comboset auto-add, the SKU modal's
// "add" button) check every option they are about to add. Holes / "" / null
// entries (an unpicked attribute) are ignored; empty set -> false.
export function anySkuOptionWmsInactive(options, inactiveIds) {
    if (!Array.isArray(options)) return false;
    return options.some((o) => skuOptionWmsInactive(o, inactiveIds));
}
